import { ColorPalette, FilterMinimalStyleCircle } from '@griply/assets';
import { IconFormNames } from '@griply/types';
import { defaultSmartListColorOption } from '@griply/utils';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Colors,
  Sizes as ButtonSizes,
  Variants,
} from 'shared/components/ui/button';
import { ColorPickerFormSelect } from 'shared/components/ui/color-picker';
import { Size as SelectSize } from 'shared/components/ui/custom-form-select';
import { Divider } from 'shared/components/ui/divider';
import { FormFieldIcon } from 'shared/components/ui/form-field-icon';
import { FormIcon } from 'shared/components/ui/form-icon';
import { Icon } from 'shared/components/ui/icon';
import {
  ResizableTextArea,
  Variants as ResizableTextAreaVariants,
} from 'shared/components/ui/resizable-text-area';
import { SmartListFormFields } from 'shared/types/smart-list-form';

import * as Styled from './smart-list-form.style';
import { useSmartListForm } from './use-smart-list-form';

export type SmartListFormProps = {
  initialValues?: SmartListFormFields;
  onSubmit: (data: SmartListFormFields) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onCancel: () => void;
  isEdit?: boolean;
  filterEditor?: React.ReactNode;
};

const defaultInitialValues: SmartListFormFields = {
  name: '',
  color: defaultSmartListColorOption.value,
  icon: IconFormNames.SmartList,
};

export const SmartListForm: React.FC<SmartListFormProps> = ({
  initialValues = {},
  onSubmit,
  onDirtyChange,
  onCancel,
  isEdit,
  filterEditor,
}) => {
  const { t } = useTranslation();
  const {
    control,
    onSubmit: submitForm,
    isValid,
    isDirty,
    setValue,
    watch,
    setFocus,
  } = useSmartListForm(onSubmit, { ...defaultInitialValues, ...initialValues });

  const color = watch('color');
  const onChangeColor = (newColor?: string) =>
    setValue<'color'>('color', newColor ?? null, { shouldDirty: true });

  const icon = watch<'icon'>('icon');
  const onChangeIcon = (newIcon: IconFormNames) =>
    setValue<'icon'>('icon', newIcon, { shouldDirty: true });

  useEffect(() => {
    if (!isEdit) {
      setFocus('name');
    }
  }, [isEdit, setFocus]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <Styled.Form noValidate onSubmit={submitForm}>
      <Styled.ScrollArea>
        <Styled.Header>
          <FormIcon
            value={icon ?? IconFormNames.SmartList}
            onChange={onChangeIcon}
            large
          />

          <Styled.TitleColumn>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <ResizableTextArea
                  ref={field.ref}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  variant={ResizableTextAreaVariants.Subtitle1}
                  placeholder={t('dialogs.create-smart-list.placeholder')}
                  fullWidth
                />
              )}
            />
          </Styled.TitleColumn>
        </Styled.Header>

        <Styled.Body>
          <Divider />

          <Styled.Section>
            <Styled.Item>
              <Styled.Label>
                <FormFieldIcon icon={ColorPalette} />
                <Styled.LabelText>{t('general.labels.color')}</Styled.LabelText>
              </Styled.Label>
              <ColorPickerFormSelect
                placeholder={t('general.labels.select')}
                color={color ?? undefined}
                onChange={onChangeColor}
                size={SelectSize.ExtraSmall}
              />
            </Styled.Item>
            {!!filterEditor && (
              <>
                <Styled.Label>
                  <Icon
                    icon={FilterMinimalStyleCircle}
                    size={16}
                    strokeWidth={1.5}
                  />
                  {t('forms.smart-list.fields.filters.label')}
                </Styled.Label>
                {filterEditor}
              </>
            )}
          </Styled.Section>
        </Styled.Body>
      </Styled.ScrollArea>

      <Styled.Footer>
        <Button
          variant={Variants.Contained}
          size={ButtonSizes.Medium}
          color={Colors.TextPrimary}
          onClick={onCancel}
          type="button"
        >
          {t('general.labels.cancel')}
        </Button>
        <Button size={ButtonSizes.Medium} type="submit" disabled={!isValid}>
          {t('general.labels.save')}
        </Button>
      </Styled.Footer>
    </Styled.Form>
  );
};
